import React from 'react';
import { InitialAvatar } from '../InitialAvatar';
import { CURRENT_MEMBER, TIMELINE_END, TIMELINE_SPAN, TIMELINE_START, overlapWith, parseYears, publicName, spanLabel } from '../../data/community';
import type { Member } from '../../data/community';
interface YearsChartProps {
  members: Member[];
  selected: Member | null;
  onSelect: (member: Member) => void;
}
const TICKS = [1976, 1986, 1996, 2006, 2016, 2026];
function position(years: string) {
  const {
    start,
    end
  } = parseYears(years);
  const left = (start - TIMELINE_START) / TIMELINE_SPAN * 100;
  const width = Math.max((end - start) / TIMELINE_SPAN * 100, 1.5);
  return {
    left: `${left}%`,
    width: `${width}%`
  };
}
const you = parseYears(CURRENT_MEMBER.years);
const yourBand = {
  left: `${(you.start - TIMELINE_START) / TIMELINE_SPAN * 100}%`,
  width: `${(you.end - you.start) / TIMELINE_SPAN * 100}%`
};

/**
 * Fifty years across, one member per row. Overlap with the signed-in member is
 * spatial — you see who was there when you were rather than reading a count.
 */
export function YearsChart({
  members,
  selected,
  onSelect
}: YearsChartProps) {
  const ordered = [...members].sort((a, b) => parseYears(a.years).start - parseYears(b.years).start);
  return <div className="flex flex-col" data-id="element-317" data-mp="8cpyckcw262l">
      <div className="sticky top-0 z-[5] flex items-end gap-3 border-b border-rule bg-card pb-2 pt-3" data-id="element-318" data-mp="cgxl4bzsdby6">
        <span className="w-24 shrink-0 text-eyebrow-caption font-semibold uppercase text-ink-muted sm:w-36" data-id="element-319" data-mp="3bwc48t3vphvd">
          {ordered.length} people
        </span>
        <div className="relative h-4 flex-1" data-id="element-320" data-mp="3gs9kkzf56o85">
          {TICKS.map(tick => <span key={tick} className="absolute -translate-x-1/2 text-eyebrow-caption font-medium text-ink-muted" style={{
          left: `${(tick - TIMELINE_START) / TIMELINE_SPAN * 100}%`
        }} data-id="element-321" data-mp="3kuxw5ev8q98t">
              {tick === TIMELINE_END ? 'now' : tick}
            </span>)}
        </div>
      </div>

      <ol className="relative flex flex-col" data-id="element-322" data-mp="4bmpjxj4xgcg">
        <div aria-hidden="true" className="pointer-events-none absolute bottom-0 top-0 ml-24 hidden sm:ml-36 sm:block" style={{
        left: 0,
        right: 0
      }} data-id="element-323" data-mp="4abquc4jqys7">
          <div className="relative h-full w-full" data-id="element-324" data-mp="e1g5tbu82jn1">
            <span className="absolute top-0 h-full rounded-sm bg-primary-soft" style={yourBand} data-id="element-325" data-mp="934q4kh8xeww" />
          </div>
        </div>

        {ordered.map(member => {
        const overlap = overlapWith(member, CURRENT_MEMBER);
        const isYou = member.name === CURRENT_MEMBER.fullName;
        const isSelected = selected?.name === member.name;
        return <li key={member.name} className="relative" data-id="element-326" data-mp="3mhjb5axv3w87">
              <button type="button" onClick={() => onSelect(member)} aria-pressed={isSelected} className={`flex min-h-tap w-full items-center gap-3 rounded-field px-1 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${isSelected ? 'bg-canvas' : 'hover:bg-canvas'}`} data-id="element-327" data-mp="3mk5ipakr6bqf">
                <span className="flex w-24 shrink-0 items-center gap-2 sm:w-36" data-id="element-328" data-mp="ze6ugdodt8wb">
                  <InitialAvatar name={member.name} size={24} data-id="element-329" data-mp="14b6ua6un1fez" />
                  <span className={`truncate text-small ${isYou ? 'font-semibold text-primary-text' : 'text-ink'}`} data-id="element-330" data-mp="1s1kjzqpr326e">
                    {isYou ? 'You' : publicName(member.name)}
                  </span>
                </span>

                <span className="relative h-9 flex-1" data-id="element-331" data-mp="1nakrcxnnz69p">
                  <span aria-hidden="true" className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-rule" data-id="element-332" data-mp="1n91951ig6l5a" />
                  {(member.spans?.length ? member.spans : [member.years]).map(segment => <span key={segment} className={`absolute top-1/2 h-2.5 -translate-y-1/2 rounded-pill ${isYou ? 'bg-primary' : overlap ? 'bg-primary-text' : 'bg-ink-subtle'}`} style={position(segment)} data-id="element-333" data-mp="s3v6qmgqn9n9" />)}
                  <span className="sr-only" data-id="element-334" data-mp="15ui5ufqev1jv">
                    {spanLabel(member)}
                    {overlap && !isYou ? `, overlapping your years from ${overlap.start} to ${overlap.end}` : ''}
                  </span>
                </span>

                <span className="hidden w-28 shrink-0 text-right text-small text-ink-muted md:inline" data-id="element-335" data-mp="1tjsxn7ps9llv">
                  {spanLabel(member).replace(/–present/g, '–now')}
                </span>
              </button>
            </li>;
      })}
      </ol>

      <p className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-rule pt-4 text-small text-ink-muted" data-id="element-336" data-mp="1x3bbyl1qci0l">
        <span className="flex items-center gap-2" data-id="element-337" data-mp="21558ch4w6zuy">
          <span aria-hidden="true" className="h-2.5 w-6 rounded-pill bg-primary" data-id="element-338" data-mp="261dhndweryu7" />
          You
        </span>
        <span className="flex items-center gap-2" data-id="element-339" data-mp="2b2axhjty4gsj">
          <span aria-hidden="true" className="h-2.5 w-6 rounded-pill bg-primary-text" data-id="element-340" data-mp="2g0kxq8uj27l0" />
          Here when you were
        </span>
        <span className="flex items-center gap-2" data-id="element-341" data-mp="2k14qz1bzcr1v">
          <span aria-hidden="true" className="h-2.5 w-6 rounded-pill bg-ink-subtle" data-id="element-342" data-mp="2jztn71ijbjar" />
          Different years
        </span>
      </p>
    </div>;
}